// Social Music — company music challenges: submit external track links, vote, see the
// leaderboard, and open tracks in the provider (validated deep-link). Provider-neutral;
// stores external references only; no embedded player, no listening data. Admins
// (recognition:manage) can create/open/close/announce challenges.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Music, Trophy, Plus, ChevronUp, ExternalLink, Loader2, Users, Clock, MessageCircle, Settings, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Badge } from '@/features/shared/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiError';
import { openProviderUrl, isSafeProviderUrl } from '@/lib/providerLinks';
import { useHasPermission } from '@/rbac/useRbac';
import * as api from '@/services/performanceService';
import { AppleMusicConnect } from './AppleMusicConnect';
import { MusicSearch } from './MusicSearch';
import { MusicHome } from './MusicHome';
import { MyPlaylists } from './MyPlaylists';
import { useMusicConnected } from './lib/useMusicConnected';
import type { MusicItem } from './lib/types';

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');
// ISO → value for <input type="datetime-local"> (local time, no seconds/zone).
const toLocalInput = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s); if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const MusicPage: React.FC = () => {
  const canManage = useHasPermission('recognition:manage');
  const [challenges, setChallenges] = useState<api.MusicChallenge[]>([]);
  const [sel, setSel] = useState<api.MusicChallenge | null>(null);
  const [subs, setSubs] = useState<api.MusicSubmission[]>([]);
  const [myVotes, setMyVotes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newEnd, setNewEnd] = useState('');
  // Inline edit of the selected challenge (title / description / deadline).
  const [editing, setEditing] = useState(false);
  const [eTitle, setETitle] = useState('');
  const [eDesc, setEDesc] = useState('');
  const [eEnd, setEEnd] = useState('');
  const [settings, setSettings] = useState<api.MusicSettings | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [tab, setTab] = useState<'home' | 'search' | 'playlists' | 'challenges'>('home');
  const [searchSeed, setSearchSeed] = useState<string | undefined>(undefined);
  const { connected: musicConnected } = useMusicConnected();

  const loadList = () => api.listMusicChallenges().then(setChallenges).catch(() => setChallenges([])).finally(() => setLoading(false));
  useEffect(() => { void loadList(); }, []);
  // Load settings; on any failure fall back to a usable default so the Settings panel
  // ALWAYS opens (admins can still enable Music for the workspace). Never leave it null,
  // which made the Settings button appear to do nothing.
  useEffect(() => {
    if (!canManage) return;
    const DEFAULT: api.MusicSettings = {
      enabled: false, playlists_enabled: true, submissions_enabled: true, voting_enabled: true,
      challenges_enabled: true, dana_enabled: true, conversations_enabled: true, provider: 'reference',
    };
    api.getMusicSettings().then((s) => setSettings(s || DEFAULT)).catch(() => setSettings(DEFAULT));
  }, [canManage]);

  const saveSettings = (patch: Partial<api.MusicSettings>) => guard(async () => {
    const next = { ...(settings as api.MusicSettings), ...patch };
    const saved = await api.updateMusicSettings(next);
    setSettings(saved);
  }, 'Music settings saved');

  const openChallenge = async (c: api.MusicChallenge) => {
    setSel(c); setSubs([]); setMyVotes([]); setEditing(false);
    try {
      const [s, v] = await Promise.all([api.listMusicSubmissions(c.id), api.myMusicVotes(c.id)]);
      setSubs(s); setMyVotes(v);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Could not load the challenge')); }
  };
  const refreshSel = async () => { if (sel) await openChallenge(sel); await loadList(); };

  const guard = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Something went wrong')); }
    finally { setBusy(false); }
  };

  const submit = () => guard(async () => {
    if (!sel) return;
    if (!isSafeProviderUrl(url.trim())) { toast.error('Paste an Apple Music, Spotify, or YouTube Music playlist/song link.'); return; }
    await api.submitMusicTrack(sel.id, { url: url.trim(), title: title.trim() });
    setUrl(''); setTitle('');
    await refreshSel();
  }, 'Submitted 🎵');

  const vote = (s: api.MusicSubmission) => guard(async () => { await api.voteMusic(s.id); await refreshSel(); });
  const openTrack = (s: api.MusicSubmission) => { if (!openProviderUrl(s.canonical_url)) toast.error('This link can’t be opened.'); };

  const createChallenge = () => guard(async () => {
    if (!newTitle.trim()) return;
    await api.createMusicChallenge({
      title: newTitle.trim(),
      description: newDesc.trim() || undefined,
      end_at: newEnd ? new Date(newEnd).toISOString() : undefined,
    } as Partial<api.MusicChallenge>);
    setNewTitle(''); setNewDesc(''); setNewEnd('');
    await loadList();
  }, 'Draft challenge created');

  const startEdit = () => {
    if (!sel) return;
    setETitle(sel.title); setEDesc(sel.description || ''); setEEnd(toLocalInput(sel.end_at)); setEditing(true);
  };
  const saveEdit = () => guard(async () => {
    if (!sel || !eTitle.trim()) return;
    const saved = await api.updateMusicChallenge(sel.id, {
      title: eTitle.trim(),
      description: eDesc.trim(),
      end_at: eEnd ? new Date(eEnd).toISOString() : null,
    } as Partial<api.MusicChallenge>);
    setEditing(false); setSel(saved); await loadList();
  }, 'Challenge updated');
  const lifecycle = (fn: (id: string) => Promise<unknown>, ok: string) => guard(async () => {
    if (!sel) return; await fn(sel.id); await refreshSel();
  }, ok);

  if (loading) return <div className="p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-charcoal"><Music className="h-6 w-6 text-primary-500" /> Music</h1>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setShowSettings((v) => !v)}><Settings className="mr-1.5 h-4 w-4" />Settings</Button>
        )}
      </div>

      {/* Personal Apple Music connection — self-hides unless enabled server-side. */}
      <AppleMusicConnect />

      {/* Workspace tabs — KaziPeople Music is the product; Apple Music is the provider. */}
      <div className="flex gap-1 border-b border-border">
        {([['home', 'Home'], ['search', 'Search'], ['playlists', 'My Playlists'], ['challenges', 'Challenges']] as const).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${tab === id ? 'border-primary-500 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Settings panel — rendered right under the tabs so the Settings button reveals it
          at the top (it used to render after all the tab content, far below the fold). */}
      {canManage && showSettings && settings && (
        <Card><CardContent className="space-y-3 pt-4">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" className="h-4 w-4 accent-orange-500" checked={settings.enabled} onChange={(e) => saveSettings({ enabled: e.target.checked })} />
            Enable Music for this workspace
          </label>
          {settings.enabled && (
            <div className="grid gap-2 pl-6 sm:grid-cols-2">
              {([
                ['challenges_enabled', 'Challenges'], ['submissions_enabled', 'Submissions'],
                ['voting_enabled', 'Voting'], ['playlists_enabled', 'Playlists'],
                ['dana_enabled', 'Dana suggestions'], ['conversations_enabled', 'Conversations sharing'],
              ] as [keyof api.MusicSettings, string][]).map(([key, lbl]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-orange-500" checked={!!settings[key]} disabled={busy}
                    onChange={(e) => saveSettings({ [key]: e.target.checked } as Partial<api.MusicSettings>)} />
                  {lbl}
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Employees see only what’s enabled. Music participation is optional; listening activity is never collected.</p>
        </CardContent></Card>
      )}

      {tab === 'home' && (
        <MusicHome
          onOpenChallenges={() => setTab('challenges')}
          onOpenChallenge={(c) => { setTab('challenges'); void openChallenge(c); }}
          onOpenSearch={(seed) => { setSearchSeed(seed); setTab('search'); }}
        />
      )}

      {tab === 'search' && (musicConnected ? (
        <Card><CardContent className="pt-4">
          <MusicSearch
            seed={searchSeed}
            onAddToChallenge={sel && sel.status === 'OPEN'
              ? (item: MusicItem) => guard(async () => {
                  await api.submitMusicTrack(sel.id, { url: item.canonicalUrl || '', title: item.title, artist: item.artist });
                  await refreshSel();
                }, 'Added to challenge 🎵')
              : undefined}
          />
        </CardContent></Card>
      ) : (
        <Card><CardContent className="pt-4 text-sm text-muted-foreground">Connect Apple Music above to search the catalog and play songs.</CardContent></Card>
      ))}

      {tab === 'playlists' && <MyPlaylists />}

      {tab === 'challenges' && (<>
      {canManage && settings && !settings.enabled && !showSettings && (
        <Card><CardContent className="flex items-center justify-between gap-3 pt-4">
          <span className="text-sm text-muted-foreground">Music is off for this workspace.</span>
          <Button size="sm" disabled={busy} onClick={() => saveSettings({ enabled: true })}>Enable Music</Button>
        </CardContent></Card>
      )}

      {canManage && settings?.enabled && (
        <Card><CardContent className="space-y-3 pt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">New challenge</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Title</label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Friday Energy" className="mt-1.5" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Deadline (optional)</label>
              <Input type="datetime-local" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Description (optional)</label>
            <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="What's the vibe? Rules, theme, prize…" className="mt-1.5" />
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={busy || !newTitle.trim()} onClick={createChallenge}><Plus className="mr-1.5 h-4 w-4" />Create draft</Button>
          </div>
        </CardContent></Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* challenge list */}
        <div className="space-y-2 lg:col-span-1">
          {challenges.length === 0 && <Card><CardContent className="pt-4 text-sm text-muted-foreground">No challenges yet.</CardContent></Card>}
          {challenges.map((c) => (
            <button key={c.id} type="button" onClick={() => openChallenge(c)}
              className={`w-full rounded-xl border p-3 text-left transition-colors ${sel?.id === c.id ? 'border-primary-400 bg-accent' : 'border-border bg-card hover:bg-muted'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{c.title}</span>
                <Badge className="border-0 bg-muted text-[10px] text-muted-foreground">{c.status}</Badge>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{c.participant_count ?? 0}</span>
                {c.end_at && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtDate(c.end_at)}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* selected challenge */}
        <div className="lg:col-span-2">
          {!sel ? (
            <Card><CardContent className="pt-6 text-sm text-muted-foreground">Select a challenge to submit songs and vote.</CardContent></Card>
          ) : (
            <Card><CardContent className="space-y-4 pt-4">
              {canManage && editing ? (
                <div className="space-y-3 rounded-lg border border-primary-300 bg-accent p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Edit challenge</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Title</label>
                      <Input value={eTitle} onChange={(e) => setETitle(e.target.value)} className="mt-1.5" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Deadline</label>
                      <Input type="datetime-local" value={eEnd} onChange={(e) => setEEnd(e.target.value)} className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Description</label>
                    <Input value={eDesc} onChange={(e) => setEDesc(e.target.value)} placeholder="What's the vibe? Rules, theme, prize…" className="mt-1.5" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
                    <Button size="sm" disabled={busy || !eTitle.trim()} onClick={saveEdit}>Save changes</Button>
                  </div>
                </div>
              ) : (
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-lg font-semibold text-foreground">{sel.title}</div>
                  {sel.description && <p className="text-sm text-muted-foreground">{sel.description}</p>}
                  {sel.end_at && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" />Deadline {fmtDate(sel.end_at)}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => guard(() => api.shareMusicChallenge(sel.id), 'Shared to Conversations 💬')}><MessageCircle className="mr-1.5 h-4 w-4" />Share</Button>
                  {canManage && (<>
                    {sel.status !== 'ANNOUNCED' && <Button size="sm" variant="outline" disabled={busy} onClick={startEdit}><Pencil className="mr-1.5 h-4 w-4" />Edit</Button>}
                    {sel.status === 'DRAFT' && <Button size="sm" variant="outline" disabled={busy} onClick={() => lifecycle(api.openMusicChallenge, 'Opened')}>Open</Button>}
                    {sel.status === 'OPEN' && <Button size="sm" variant="outline" disabled={busy} onClick={() => lifecycle(api.closeMusicChallenge, 'Closed')}>Close</Button>}
                    {(sel.status === 'OPEN' || sel.status === 'CLOSED') && <Button size="sm" variant="outline" disabled={busy} onClick={() => lifecycle(api.announceMusicChallenge, 'Winners announced 🏆')}><Trophy className="mr-1.5 h-4 w-4" />Announce</Button>}
                  </>)}
                </div>
              </div>
              )}

              {sel.status === 'OPEN' && (
                <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/40 p-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-semibold text-muted-foreground">Submit a song / playlist link</label>
                    <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://open.spotify.com/playlist/…" className="mt-1.5" />
                  </div>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="w-40" />
                  <Button size="sm" disabled={busy || !url.trim()} onClick={submit}><Plus className="mr-1.5 h-4 w-4" />Submit</Button>
                </div>
              )}

              <div>
                <div className="mb-2 text-xs font-semibold text-muted-foreground">Top songs</div>
                <div className="space-y-2">
                  {subs.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
                  {subs.map((s, i) => {
                    const voted = myVotes.includes(s.id);
                    return (
                      <div key={s.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5">
                        <span className="w-5 text-center text-sm font-semibold text-muted-foreground">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">{s.title || s.canonical_url}</div>
                          <div className="truncate text-xs text-muted-foreground">{s.artist || s.provider.replace('_', ' ')}</div>
                        </div>
                        <button type="button" aria-label="Open in provider" title="Open in provider" onClick={() => openTrack(s)}
                          className="rounded p-1.5 text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></button>
                        <Button size="sm" variant={voted ? 'default' : 'outline'} disabled={busy || sel.status !== 'OPEN'} onClick={() => vote(s)}
                          aria-pressed={voted} aria-label={`Vote — ${s.vote_count} votes`}>
                          <ChevronUp className="mr-1 h-4 w-4" />{s.vote_count}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent></Card>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
};
